import { useEffect, useId, useRef, useState } from "react";
import { Columns2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

export function HubDataTableColumnLayoutMenu({
  columnIds,
  hiddenColumnIds,
  onToggleColumn,
  onResetLayout,
}: {
  columnIds: readonly string[];
  hiddenColumnIds: ReadonlySet<string>;
  onToggleColumn: (columnId: string, visible: boolean) => void;
  onResetLayout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const hiddenList = [...hiddenColumnIds];

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
      >
        <Columns2 className="mr-1.5 h-4 w-4" aria-hidden />
        Columns
        {hiddenList.length > 0 ? ` (${columnIds.length - hiddenList.length}/${columnIds.length})` : ""}
      </Button>
      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Table columns"
          className="absolute right-0 z-30 mt-1 w-64 max-h-80 overflow-auto rounded-md border border-slate-200 bg-white py-2 shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 pb-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Show columns</span>
            <button
              type="button"
              className="text-xs text-brand-700 hover:underline"
              onClick={() => {
                onResetLayout();
                setOpen(false);
              }}
            >
              Reset
            </button>
          </div>
          <ul className="px-1 py-1">
            {columnIds.map((columnId) => {
              const visible = !hiddenColumnIds.has(columnId);
              return (
                <li key={columnId}>
                  <label
                    className={cn(
                      "flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-50",
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={visible}
                      onChange={(event) => onToggleColumn(columnId, event.target.checked)}
                    />
                    <span className="min-w-0 break-words font-mono text-xs leading-snug">{columnId}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          <p className="border-t border-slate-100 px-3 pt-2 text-xs text-slate-500">
            Drag column headers to reorder. Layout is saved to your hub profile.
          </p>
        </div>
      ) : null}
    </div>
  );
}
